import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Save, X, Image as ImageIcon, DollarSign, BookOpen, Award, Target, Settings } from "lucide-react"
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import { useCourseCreation } from "../../hooks/useCourseCreation"
import { useCategories } from "../../hooks/useCategories"
import ImageUpload from "../../components/ImageUpload"
import { Button } from "../../components/ui/button"
import { logger } from "../../utils/logger"
import { showSuccessToast, showErrorToast } from "../../utils/toast"

export default function CreateCoursePage() {
  const navigate = useNavigate()
  const { createCourse, loading, error } = useCourseCreation()
  const { categories, loading: categoriesLoading } = useCategories()

  const [formData, setFormData] = useState({
    // Champs obligatoires
    title: '',
    description: '',
    price: 0,
    categoryId: '',

    // Champs optionnels
    shortDescription: '',
    level: 'BEGINNER',
    language: 'Français',
    currency: 'XOF',
    status: 'DRAFT',
    isFree: false,
    isFeatured: false,
    isPublished: false,
    maxStudents: 100,
    certificate: false,
    accessDurationDays: 90,
    isUnlimitedAccess: false,
    duration: 0,
    originalPrice: 0,

    // Arrays
    requirements: [],
    objectives: [],
    tagIds: []
  })

  const [thumbnailFile, setThumbnailFile] = useState(null)
  const [errors, setErrors] = useState({})
  const [currentRequirement, setCurrentRequirement] = useState('')
  const [currentObjective, setCurrentObjective] = useState('')

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }))
    // Effacer l'erreur pour ce champ
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  const handleAddArrayItem = (field, value, setter) => {
    if (!value.trim()) return
    
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], value.trim()]
    }))
    setter('')
  }

  const handleRemoveArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.title.trim()) {
      newErrors.title = "Le titre est requis"
    }

    if (!formData.description.trim()) {
      newErrors.description = "La description est requise"
    } else if (formData.description.length < 10) {
      newErrors.description = "La description doit contenir au moins 10 caractères"
    }

    if (formData.price === undefined || formData.price === null || formData.price < 0) {
      newErrors.price = "Le prix doit être un nombre positif"
    }

    if (!formData.categoryId) {
      newErrors.categoryId = "La catégorie est requise"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      showErrorToast({ message: "Veuillez corriger les erreurs du formulaire" })
      return
    }

    try {
      // Préparer les données avec les fichiers
      const { isUnlimitedAccess, ...dataToSend } = formData
      const courseData = {
        ...dataToSend,
        thumbnailFile, // File object (sera détecté par prepareCourseData)
        accessDurationDays: isUnlimitedAccess ? null : formData.accessDurationDays
        // Si pas de fichiers mais URLs, on peut les ajouter ici
      }

      logger.debug('Creating course', {
        title: courseData.title,
        hasThumbnail: !!thumbnailFile
      })

      const newCourse = await createCourse(courseData)

      if (newCourse) {
        logger.info('Course created successfully', { courseId: newCourse.id })
        showSuccessToast({ message: "Cours créé avec succès !" })
        navigate(`/admin/courses/${newCourse.id}/edit`)
      }
    } catch (err) {
      logger.error('Error creating course', err, {
        title: formData.title
      })
      showErrorToast({
        message: err?.message || "Erreur lors de la création du cours"
      })
    }
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <AdminHeader 
          title="Créer un nouveau cours" 
          subtitle="Remplissez les informations pour créer un cours"
        />
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations de base */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BookOpen className="w-5 h-5 mr-2" />
              Informations de base
            </h2>
            
            <div className="space-y-4">
              {/* Titre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre du cours *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                    errors.title ? "border-gray-500" : "border-gray-300"
                  }`}
                  placeholder="Ex: Apprendre React de A à Z"
                />
                {errors.title && (
                  <p className="text-gray-700 text-sm mt-1">{errors.title}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description complète *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={6}
                  minLength={10}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                    errors.description ? "border-gray-500" : "border-gray-300"
                  }`}
                  placeholder="Décrivez votre cours en détail (minimum 10 caractères)..."
                />
                {errors.description && (
                  <p className="text-gray-700 text-sm mt-1">{errors.description}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  {formData.description.length} / 10 caractères minimum
                </p>
              </div>

              {/* Description courte */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description courte
                </label>
                <input
                  type="text"
                  name="shortDescription"
                  value={formData.shortDescription}
                  onChange={handleChange}
                  maxLength={150}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                  placeholder="Une description courte pour la carte du cours (max 150 caractères)"
                />
                <p className="text-gray-500 text-xs mt-1">
                  {formData.shortDescription.length} / 150 caractères
                </p>
              </div>

              {/* Catégorie */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie *
                </label>
                <select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  required
                  disabled={categoriesLoading}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                    errors.categoryId ? "border-gray-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.displayName || category.translatedName || category.name}
                    </option>
                  ))}
                </select>
                {errors.categoryId && (
                  <p className="text-gray-700 text-sm mt-1">{errors.categoryId}</p>
                )}
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ImageIcon className="w-5 h-5 mr-2" />
              Images
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Thumbnail */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image miniature (Thumbnail)
                </label>
                <ImageUpload
                  onImageSelect={(file) => {
                    setThumbnailFile(file)
                    logger.debug('Thumbnail selected', { fileName: file?.name, fileSize: file?.size })
                  }}
                  currentImage={null}
                  placeholder="Cliquez pour sélectionner une image miniature"
                />
                {thumbnailFile && (
                  <p className="text-green-600 text-xs mt-2">
                    {thumbnailFile.name} ({(thumbnailFile.size / 1024).toFixed(2)} KB)
                  </p>
                )}
                <p className="text-gray-500 text-xs mt-2">
                  Format recommandé: 16:9, minimum 1280x720px
                </p>
              </div>

            </div>
          </div>

          {/* Prix et tarification */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2" />
              Tarification
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Prix */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prix (en centimes) *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  min="0"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                    errors.price ? "border-gray-500" : "border-gray-300"
                  }`}
                  placeholder="9900"
                />
                {errors.price && (
                  <p className="text-gray-700 text-sm mt-1">{errors.price}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Ex: 9900 = 99.00 {formData.currency}
                </p>
              </div>

              {/* Prix original */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prix original (centimes)
                </label>
                <input
                  type="number"
                  name="originalPrice"
                  value={formData.originalPrice}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                  placeholder="19900"
                />
              </div>

              {/* Devise */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Devise
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                >
                  <option value="XOF">XOF (Franc CFA)</option>
                </select>
              </div>
            </div>

            {/* Cours gratuit */}
            <div className="mt-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="isFree"
                  checked={formData.isFree}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                />
                <span className="text-sm font-medium text-gray-700">
                  Cours gratuit
                </span>
              </label>
              {formData.isFree && (
                <p className="text-gray-500 text-xs mt-1 ml-6">
                  Le prix sera automatiquement défini à 0
                </p>
              )}
            </div>
          </div>

          {/* Paramètres du cours */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Paramètres du cours
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Niveau */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Niveau
                </label>
                <select
                  name="level"
                  value={formData.level}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                >
                  <option value="BEGINNER">Débutant</option>
                  <option value="INTERMEDIATE">Intermédiaire</option>
                  <option value="ADVANCED">Avancé</option>
                  <option value="EXPERT">Expert</option>
                </select>
              </div>

              {/* Langue */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Langue
                </label>
                <select
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                >
                  <option value="Français">Français</option>
                  <option value="English">English</option>
                </select>
              </div>

              {/* Durée (en secondes) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée (en secondes)
                </label>
                <input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                  placeholder="1800"
                />
                <p className="text-gray-500 text-xs mt-1">
                  {formData.duration ? `${Math.floor(formData.duration / 60)} minutes` : '0 minutes'}
                </p>
              </div>

              {/* Durée d'accès */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée d'accès (jours)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    name="accessDurationDays"
                    value={formData.accessDurationDays}
                    onChange={handleChange}
                    min="1"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                    placeholder="90"
                    disabled={formData.isUnlimitedAccess}
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="isUnlimitedAccess"
                      checked={formData.isUnlimitedAccess}
                      onChange={handleChange}
                      className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                    />
                    <label className="text-sm text-gray-700">Illimité</label>
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {formData.isUnlimitedAccess
                    ? "L'accès au cours est illimité (accessExpiresAt sera null)"
                    : "L'accès expirera après le nombre de jours spécifié"}
                </p>
              </div>

              {/* Max étudiants */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre maximum d'étudiants
                </label>
                <input
                  type="number"
                  name="maxStudents"
                  value={formData.maxStudents}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                  placeholder="100"
                />
              </div>

              {/* Statut */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                >
                  <option value="DRAFT">Brouillon</option>
                  <option value="PUBLISHED">Publié</option>
                  <option value="ARCHIVED">Archivé</option>
                </select>
              </div>
            </div>

            {/* Options */}
            <div className="mt-4 space-y-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="isPublished"
                  checked={formData.isPublished}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                />
                <span className="text-sm font-medium text-gray-700">
                  Publié
                </span>
              </label>
              
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="isFeatured"
                  checked={formData.isFeatured}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                />
                <span className="text-sm font-medium text-gray-700">
                  Mettre en vedette
                </span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="certificate"
                  checked={formData.certificate}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                />
                <span className="text-sm font-medium text-gray-700">
                  Certificat de completion inclus
                </span>
              </label>
            </div>
          </div>

          {/* Prérequis */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Target className="w-5 h-5 mr-2" />
              Prérequis
            </h2>
            
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={currentRequirement}
                onChange={(e) => setCurrentRequirement(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddArrayItem('requirements', currentRequirement, setCurrentRequirement)
                  }
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                placeholder="Ajouter un prérequis"
              />
              <Button
                type="button"
                onClick={() => handleAddArrayItem('requirements', currentRequirement, setCurrentRequirement)}
                variant="outline"
              >
                Ajouter
              </Button>
            </div>
            
            {formData.requirements.length > 0 && (
              <div className="space-y-2">
                {formData.requirements.map((req, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm text-gray-700">{req}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveArrayItem('requirements', index)}
                      className="text-gray-700 hover:text-gray-900"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Objectifs */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Award className="w-5 h-5 mr-2" />
              Objectifs d'apprentissage
            </h2>
            
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={currentObjective}
                onChange={(e) => setCurrentObjective(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddArrayItem('objectives', currentObjective, setCurrentObjective)
                  }
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                placeholder="Ajouter un objectif"
              />
              <Button
                type="button"
                onClick={() => handleAddArrayItem('objectives', currentObjective, setCurrentObjective)}
                variant="outline"
              >
                Ajouter
              </Button>
            </div>
            
            {formData.objectives.length > 0 && (
              <div className="space-y-2">
                {formData.objectives.map((obj, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm text-gray-700">{obj}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveArrayItem('objectives', index)}
                      className="text-gray-700 hover:text-gray-900"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Erreurs */}
          {error && (
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
              <p className="text-gray-800">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/courses')}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                  Création en cours...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Créer le cours
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
